/**
 * ErrorRecovery.js - Centralized error handling for co-pilot chat
 *
 * Parses HTTP error responses from the unified copilot API and returns
 * structured info so each consumer can show the right message.
 *
 * Pattern: check status code first, then parse detail string.
 */
import { toast } from 'sonner';

/**
 * Parse a copilot API error into a structured result.
 * @param {Error} error - Axios error from a copilot API call
 * @returns {{ isBudgetExceeded, isUpgradeRequired, isSeatLimit, message, canRetry }}
 */
export const parseCopilotError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail || '';
  const detailLower = typeof detail === 'string' ? detail.toLowerCase() : '';

  if (status === 402) {
    if (detailLower.includes('token budget')) {
      return {
        isBudgetExceeded: true,
        isUpgradeRequired: false,
        isSeatLimit: false,
        message: detail,
        canRetry: false,
      };
    }
    if (detailLower.includes('seat_limit') || detailLower.includes('seat limit')) {
      return {
        isBudgetExceeded: false,
        isUpgradeRequired: false,
        isSeatLimit: true,
        message: 'Seat limit exceeded. Contact your account owner to upgrade.',
        canRetry: false,
      };
    }
    if (detailLower.includes('upgrade')) {
      return {
        isBudgetExceeded: false,
        isUpgradeRequired: true,
        isSeatLimit: false,
        message: 'Plan upgrade required. Contact your account owner.',
        canRetry: false,
      };
    }
    return {
      isBudgetExceeded: false,
      isUpgradeRequired: false,
      isSeatLimit: false,
      message: detail || 'Payment required. Please check your account settings.',
      canRetry: false,
    };
  }

  // Everything else is retryable
  return {
    isBudgetExceeded: false,
    isUpgradeRequired: false,
    isSeatLimit: false,
    message: detail || 'Something went wrong. Please try again.',
    canRetry: true,
  };
};

/**
 * Show a toast for a copilot error. Handles budget, seat, upgrade, and generic.
 * @param {Error} error - Axios error
 * @param {{ isOwner?: boolean }} options
 */
export const showCopilotErrorToast = (error, { isOwner = false } = {}) => {
  const parsed = parseCopilotError(error);

  if (parsed.isBudgetExceeded) {
    if (isOwner) {
      toast.error(
        'Your AI token budget has been exhausted. Add more tokens in Settings → Billing to continue using AI features.',
        { duration: 8000 }
      );
    } else {
      toast.error(
        'AI token budget exhausted. Please contact your account owner to add more tokens.',
        { duration: 8000 }
      );
    }
    return parsed;
  }

  if (parsed.isSeatLimit || parsed.isUpgradeRequired) {
    toast.error(parsed.message, { duration: 6000 });
    return parsed;
  }

  // Generic / retryable
  toast.error(parsed.canRetry
    ? 'Failed to get AI response. Please try again.'
    : parsed.message,
    { duration: 4000 }
  );
  return parsed;
};

/**
 * Full error handler: parse, toast, restore message, remove optimistic chat entry.
 */
export const handleAIChatError = (error, messageText, setInput, removeLastMessage = null) => {
  console.error('AI chat error:', error);

  const parsed = showCopilotErrorToast(error);

  if (removeLastMessage) removeLastMessage();
  if (setInput && messageText) setInput(messageText);

  return parsed;
};

export default handleAIChatError;
